/**
 * homeQa — вопросы и ответы главной страницы.
 *
 * 🔴 История и риск. Раньше здесь лежали четырнадцать полноразмерных руководств,
 * перенесённых с WordPress: 17 741 слово из 18 841 на главной жили в этом
 * аккордеоне, и именно они держали главную в выдаче по «estate planning attorney
 * nyc», «probate attorney bronx» и остальному набору гео+практика (3 257 кликов
 * и 1,27 млн показов за 90 дней). Прежняя версия файла прямо предупреждала:
 * сокращение отдаёт эти позиции.
 *
 * 30.08.2026 по решению клиента (пункт 19 юридического ревью) длинные тексты
 * ПЕРЕНЕСЕНЫ, а не удалены: они целиком лежат в `practiceDeepDives.ts` и
 * выводятся на страницах практик, к которым относятся. Здесь остались шесть
 * коротких ответов со ссылкой на разбор. Позиции главной по этим запросам
 * следует смотреть в Search Console: если просядут — текст возвращается одним
 * откатом коммита, содержание никуда не делось.
 *
 * `answer` — короткий ответ, он же уходит в схему FAQPage (Home.tsx).
 * `blocks` — два-три абзаца развёртки. `readMore` — ссылка на полный разбор.
 */
export type QaBlockTag = 'h2' | 'h3' | 'h4' | 'p' | 'li';

export interface QaBlock {
  tag: QaBlockTag;
  text: string;
}

export interface HomeQaItem {
  question: string;
  /** Short answer mirrored into FAQPage schema. */
  answer: string;
  blocks: QaBlock[];
  readMore?: { href: string; label: string };
}

export const homeQa: HomeQaItem[] = [
  {
    question: "How much does estate planning cost in NYC?",
    answer:
      "A basic Will starts in the low hundreds, a comprehensive Will for a homeowner with children runs higher, and a full Revocable Living Trust package for an individual generally ranges from $3,500 to $6,000 in New York City. The cheaper document is rarely the cheaper plan: a Will guarantees probate, and probate adds court filing fees of up to $1,250, statutory executor commissions and legal fees that commonly reach five figures.",
    blocks: [
      { tag: "p", text: "The honest answer depends on what the plan has to survive. A single person with one bank account needs far less than a Queens homeowner with minor children and a business interest." },
      { tag: "p", text: "What matters more than the quoted fee is the backend cost. A Will does not avoid Surrogate's Court — it sends your family there, and that process typically runs twelve to eighteen months. A Trust costs more to draft and less to administer." },
    ],
    readMore: { href: "/estate-planning/", label: "Full cost breakdown on our Estate Planning page" },
  },
  {
    question: "What is probate, and is it required if there is a will?",
    answer:
      "Probate is the Surrogate's Court proceeding that proves a Will is valid and authorizes the executor to act. Having a Will does not avoid it — a Will is the instruction manual the court reads, so it guarantees probate rather than preventing it. Assets held in a properly funded trust, or passing by beneficiary designation, are what stay out of court.",
    blocks: [
      { tag: "p", text: "When someone dies owning property, bank accounts or a business in their own name, those assets are frozen until the court appoints someone with authority over them. That appointment is what probate is for." },
      { tag: "p", text: "The widespread belief that a Will lets the family bypass the court is simply wrong, and it is the single most expensive misunderstanding we correct." },
    ],
    readMore: { href: "/probate/", label: "How probate works in New York" },
  },
  {
    question: "Which is better — a will or a trust?",
    answer:
      "Neither is better in the abstract; they do different jobs. A Will directs who receives what and takes effect through the court after death. A Revocable Living Trust holds the assets during life, so they pass without probate, and it also covers incapacity — which a Will cannot do at all.",
    blocks: [
      { tag: "p", text: "For most New York homeowners the practical question is not \u201cWill or Trust\u201d but \u201cWill alone, or Trust with a Pour-Over Will\u201d. Real estate in this state makes court administration expensive enough that avoiding it usually pays for itself." },
      { tag: "p", text: "A Trust only works if it is funded: the deed and the accounts must actually be retitled into it. An unfunded trust is an expensive folder." },
    ],
    readMore: { href: "/trusts/", label: "Wills, trusts and how they work together" },
  },
  {
    question: "Who inherits if someone dies without a will in New York?",
    answer:
      "State intestacy law decides, not the family. New York's rules under the EPTL distribute the estate by fixed shares to the surviving spouse and children — and where there is no spouse or child, outward to parents, siblings and more distant relatives. Unmarried partners and stepchildren receive nothing.",
    blocks: [
      { tag: "p", text: "The court also chooses who administers the estate, following the same order of priority. That is often not the person the family would have picked." },
      { tag: "p", text: "This is the outcome we see most often, and it is entirely avoidable with a document that takes an afternoon to sign." },
    ],
    readMore: { href: "/probate/", label: "Intestacy and estate administration" },
  },
  {
    question: "How can I protect assets from nursing-home and long-term-care costs?",
    answer:
      "The main tool in New York is an irrevocable Medicaid Asset Protection Trust, and it carries a five-year look-back: transfers made within five years of applying for institutional Medicaid can create a penalty period. Planning early is what makes the protection work; planning during a crisis leaves far fewer options.",
    blocks: [
      { tag: "p", text: "Home care and nursing care are treated differently under New York's rules, and the difference matters for timing." },
      { tag: "p", text: "There are steps available even after a diagnosis, but they are narrower and more expensive than the ones available five years earlier." },
    ],
    readMore: { href: "/elder-law/", label: "Elder law and Medicaid planning" },
  },
  {
    question: "Is estate planning the same as writing a will?",
    answer:
      "No. A Will is one document inside a plan. A complete New York plan also covers what happens while you are alive — a Power of Attorney for finances, a Health Care Proxy for medical decisions — plus beneficiary designations and, where appropriate, a trust that keeps assets out of court.",
    blocks: [
      { tag: "p", text: "A Will is useless the moment you need it most while living: if you suffer a stroke, no Will gives anyone authority to pay your bills or speak to your doctors." },
      { tag: "p", text: "That is why we treat incapacity documents as the foundation of a plan rather than an add-on." },
    ],
    readMore: { href: "/estate-planning/", label: "What a complete estate plan includes" },
  },
];
